if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends HTMLUListElement {
      constructor() {
        super();

        this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        this.controls.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          panel?.removeAttribute('open');
        });

        const target = event.currentTarget;
        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        panel?.setAttribute('open', '');
      }

      reset() {
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.addEventListener('cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
    
        document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
        document.removeEventListener('cart:refresh', this.onCartRefreshListener);
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <p class="drawer__empty-text h2 heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</p>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
        window.MetalyserDelivery?.schedule();

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends HTMLElement {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        // Delivery selectors are independent of the product quantity controls.
        if (!event.target.matches('input[name="updates[]"]')) return;
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        if (typeof event.cart.sections === 'undefined') return;
        const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', 'immediate');
                cartDiscount.setAttribute('active', '');
              }
            }
            miniCart.innerHTML = updatedElement.innerHTML;
          }
        }

        const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
          if (updatedElement) {
            if (event.source === 'cart-discount') {
              const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
              if (cartDiscount) {
                cartDiscount.hidden = false
                cartDiscount.setAttribute('open', '');
                cartDiscount.setAttribute('aria-expanded', 'true');
              }
            }
            
            const scrollTop = window.scrollY;
            mainCart.innerHTML = updatedElement.innerHTML;

            if (event.source === 'cart-discount') {
              requestIdleCallback(() => {
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'instant'
                });
              });
              
            }
          }
          else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        if (event.source !== 'metalyser-delivery') {
        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
        }
        else if (event.cart.item_count === 0) {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
            : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
        }
        else {
          miniCart
            ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
            : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
        }

        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        if (window.MetalyserDelivery?.active()) {
          return window.MetalyserDelivery.updateQuantity(this, line, quantity, name, target);
        }
        this.enableLoading(line);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      enableLoading(line) {
        this.classList.add('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        this.classList.remove('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends HTMLElement {
      constructor() {
        super();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLFormElement {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onSubmitHandler);
      }

      onSubmit(event) {
        event.preventDefault();

        this.abortController?.abort();
        this.abortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.classList.remove('alert--success');
        this.resultsElement.classList.add('alert--error');
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${theme.shippingCalculatorStrings.error}</p>
          <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        if (shippingRates.length) {
          this.resultsElement.classList.remove('alert--error');
          this.resultsElement.classList.add('alert--success');
        }
        else {
          this.resultsElement.classList.remove('alert--success');
          this.resultsElement.classList.add('alert--error');
        }
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
          ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
        `;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLFormElement {
      constructor() {
        super();

        this.onApplyDiscount = this.applyDiscount.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }
      
      connectedCallback() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.submitButton.addEventListener('click', this.onApplyDiscount);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButton.removeEventListener('click', this.onApplyDiscount);
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.querySelector('[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement) || typeof this.getAttribute('data-section-id') !== 'string') return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        const discountCodeValue = discountCode.value.trim();
        if (discountCodeValue === '') return;

        const existingDiscounts = this.existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        this.setDiscountError('');
        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (
              parsedState.discount_codes.find((discount) => {
                return discount.code === discountCodeValue && discount.applicable === false;
              })
            ) {
              discountCode.value = '';
              this.setDiscountError(theme.discountStrings.error);
              return;
            }

            const newHtml = parsedState.sections[this.sectionId];
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${this.sectionId}`);
            if (section) {
              const discountCodes = section?.querySelectorAll('button[is="discount-remove"]') || [];
              const codes = Array.from(discountCodes)
                .map((element) => (element instanceof HTMLButtonElement ? element.getAttribute('data-discount') : null))
                .filter(Boolean);

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                parsedState.discount_codes.find((discount) => {
                  return discount.code === discountCodeValue && discount.applicable === true;
                })
              ) {
                discountCode.value = '';
                this.setDiscountError(theme.discountStrings.shippingError);
                return;
              }
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      removeDiscount(event) {
        if ((event instanceof KeyboardEvent && event.key !== 'Enter') || !(event instanceof MouseEvent)) {
          return;
        }

        const discountCode = event.target.getAttribute('data-discount');
        if (!discountCode) return;

        const existingDiscounts = this.existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setDiscountError('');
        event.target.setAttribute('loading', '');

        const body = JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.abortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          })
          .finally(() => {
            event.target.removeAttribute('loading');
          });
      }

      existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('button[is="discount-remove"]');
        for (const pill of discountPills) {
          if (pill.hasAttribute('data-discount')) {
            discountCodes.push(pill.getAttribute('data-discount'));
          }
        }
        return discountCodes;
      }

      setDiscountError(error) {
        this.resultsElement.lastElementChild.textContent = error;
        this.resultsElement.hidden = error.length === 0;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('discount-remove')) {
  customElements.define(
    'discount-remove',
    class DiscountRemove extends MagnetButton {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');

        if (form) {
          event.preventDefault();
          form.removeDiscount(event);
        }
      }
    }, { extends: 'button' }
  );
}

/* METALYSER — optional upstairs delivery, EUR 40 per selected unit.
   Requires the non-shipping Shopify product with handle subida-a-domicilio.
   Shopify remains authoritative for all product prices and shipping rates. */
(function () {
  if (window.MetalyserDelivery) return;
  const QTY = '_metalyser_subida_qty';
  const FIT = '_metalyser_acceso';
  const HANDLE = 'subida-a-domicilio';
  const PRICE = 4000;
  // Eligibility is rendered by Liquid from product collections/tags, never from editable line properties.
  const eligibleProducts = new Map();
  function readEligibility(root) {
    for (const node of root.querySelectorAll('[data-md-eligibility]')) {
      try {
        const records = JSON.parse(node.textContent);
        if (!Array.isArray(records)) continue;
        for (const entry of records) if (entry.id && typeof entry.eligible === 'boolean') eligibleProducts.set(String(entry.id), entry.eligible);
      } catch (_) { /* Missing/invalid metadata is checked before any fee mutation. */ }
    }
  }
  function isEligible(item) {
    return !isService(item) && item.requires_shipping && !item.gift_card && eligibleProducts.get(String(item.product_id)) === true;
  }
  const MODE = 'Entrega - Modalidad';
  function hasPhysical(cart) { return cart.items.some(item => item.requires_shipping && !isService(item)); }
  function deliveryMode(cart) {
    const mode = cart.attributes?.[MODE];
    return ['envio', 'recogida'].includes(mode) ? mode : (totalUnits(cart) ? 'envio' : '');
  }
  const ATTRS = ['Entrega - Vivienda', 'Entrega - Planta', 'Entrega - Ascensor', 'Entrega - Escalones previos', 'Entrega - Detalles'];
  const fitNames = { si: 'Sí, cabe por todo el recorrido', escaleras: 'Subida por escaleras: acceso comprobado', consulta: 'Pendiente de revisar el acceso' };
  function units(item) {
    const raw = Number(item.properties?.[QTY] || 0);
    return Number.isFinite(raw) ? Math.min(item.quantity, Math.max(0, Math.floor(raw))) : 0;
  }
  function isService(item) { return item.handle === HANDLE; }
  function selectedItems(cart) { return cart.items.filter(item => isEligible(item) && units(item) > 0); }
  function totalUnits(cart) { return selectedItems(cart).reduce((sum, item) => sum + units(item), 0); }
  function deliveryProperties(item, count, fit) {
    return { ...item.properties, _metalyser_delivery_id: item.properties?._metalyser_delivery_id || ('md-' + (globalThis.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2))), [QTY]: String(count), [FIT]: count ? fit : '',
      'Subida al domicilio': count ? count + ' de ' + item.quantity + ' unidad(es)' : '',
      'Acceso comprobado': count ? (fitNames[fit] || 'Pendiente de confirmar') : '' };
  }
  function validate(cart) {
    if (hasPhysical(cart) && !deliveryMode(cart)) return { message: 'Elige si quieres recoger el pedido o recibirlo en tu dirección.', mode: true };
    if (deliveryMode(cart) === 'recogida' && (totalUnits(cart) || cart.items.some(isService))) return { message: 'Estamos quitando el suplemento de subida. Actualiza el carrito antes de continuar.', mode: true };
    const selected = selectedItems(cart);
    if (!selected.length) return null;
    for (const item of selected) {
      if (!['si', 'escaleras'].includes(item.properties?.[FIT])) {
        return { message: 'Comprueba el acceso de «' + item.product_title + '». Si no sabes si cabe, consúltanos por WhatsApp antes de pagar.', key: item.key };
      }
    }
    const a = cart.attributes || {};
    if (!['Piso', 'Casa', 'Ático', 'Local', 'Otro'].includes(a[ATTRS[0]])) return { message: 'Selecciona el tipo de vivienda.', field: ATTRS[0] };
    const floor = Number(a[ATTRS[1]]);
    if (String(a[ATTRS[1]] ?? '').trim() === '' || !Number.isInteger(floor) || floor < -3 || floor > 100) return { message: 'Indica la planta de entrega (0 para planta baja).', field: ATTRS[1] };
    if (!['Sí', 'No', 'No hace falta (planta baja)'].includes(a[ATTRS[2]])) return { message: 'Indica si hay ascensor.', field: ATTRS[2] };
    if (floor !== 0 && a[ATTRS[2]] === 'No hace falta (planta baja)') return { message: 'Para esta planta, indica si hay ascensor o no.', field: ATTRS[2] };
    if (!['Sí', 'No'].includes(a[ATTRS[3]])) return { message: 'Indica si hay escalones antes de la entrada o del ascensor.', field: ATTRS[3] };
    return null;
  }
  class Delivery {
    constructor() {
      this.tail = Promise.resolve(); this.pending = 0; this.rendering = false;
      this.drafts = {}; this.lastCart = null; this.checkoutRunning = false;
      this.init();
    }
    config() {
      const node = document.querySelector('[data-md-config]');
      try { return node ? JSON.parse(node.textContent) : null; } catch (_) { return null; }
    }
    active() {
      const config = this.config();
      return !!config && (config.ready || !!document.querySelector('[data-md-line], [data-md-service-line], [data-md-mode]'));
    }
    route(action) {
      const root = window.Shopify?.routes?.root || theme.routes.root_url || '/';
      return root.replace(/\/?$/, '/') + 'cart' + (action ? '/' + action : '') + '.js';
    }
    async request(action, body) {
      const response = await fetch(this.route(action), {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400 || data.errors) {
        throw new Error(typeof data.description === 'string' ? data.description : 'No se ha podido actualizar el carrito. Revisa la conexión y vuelve a intentarlo.');
      }
      return data;
    }
    getCart() { return this.request(''); }
    async loadEligibility(cart) {
      readEligibility(document);
      const missing = () => cart.items.some(item => item.requires_shipping && !isService(item) && !eligibleProducts.has(String(item.product_id)));
      if (!missing()) return;
      // Newly added rows may not have rendered yet. Request only their current section metadata.
      const sections = this.sectionIds();
      if (!sections.length) throw new Error('Actualiza el carrito para comprobar las opciones de entrega de cada producto.');
      const html = await this.readSections(sections);
      for (const id of sections) if (typeof html[id] === 'string') readEligibility(new DOMParser().parseFromString(html[id], 'text/html'));
      if (missing()) throw new Error('Actualiza los archivos del carrito para comprobar qué productos admiten subida a domicilio.');
    }
    setBusy(busy) {
      document.documentElement.classList.toggle('md-cart-busy', busy);
      document.querySelectorAll('[data-md-qty], [data-md-fit], [data-md-mode], [data-md-delivery-choice], [data-md-count]').forEach(el => { el.disabled = busy || el.hasAttribute('data-md-unavailable'); });
      document.querySelectorAll('[data-md-panel]').forEach(el => el.setAttribute('aria-busy', String(busy)));
    }
    status(message, error = false) {
      document.querySelectorAll('[data-md-status]').forEach(el => {
        el.textContent = message; el.setAttribute('role', error ? 'alert' : 'status');
      });
      document.querySelectorAll('[data-md-retry]').forEach(el => { el.hidden = !error; });
      // This fallback is needed if the service becomes unavailable while in the cart.
      if (message && !document.querySelector('[data-md-status]')) {
        let el = document.querySelector('[data-md-fallback-status]');
        if (!el) {
          el = document.createElement('p'); el.dataset.mdFallbackStatus = '';
          el.style.cssText = 'padding:12px;margin:12px 0;color:#8b2525;background:#fff4f0;border-radius:8px;font-size:14px;';
          const button = document.querySelector('[name="checkout"]');
          button?.parentElement?.insertBefore(el, button);
        }
        el.textContent = message; el.setAttribute('role', error ? 'alert' : 'status');
      }
    }
    sectionIds() {
      const ids = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: ids } }));
      return [...new Set(ids.filter(Boolean))].slice(0, 5);
    }
    async persistDrafts(cart) {
      const attributes = { ...this.drafts };
      if (!Object.keys(attributes).length) return cart;
      const result = await this.request('update', { attributes });
      for (const [key, value] of Object.entries(attributes)) if (this.drafts[key] === value) delete this.drafts[key];
      return result;
    }
    async reconcile(cart) {
      await this.loadEligibility(cart);
      // Correct partial selections after a regular quantity change. Preserve every other property.
      let repairs = 0;
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        if (isService(item) || !Object.prototype.hasOwnProperty.call(item.properties || {}, QTY)) continue;
        const count = deliveryMode(cart) === 'recogida' ? 0 : (isEligible(item) ? units(item) : 0);
        const fit = item.properties?.[FIT] || '';
        const expected = deliveryProperties(item, count, fit);
        if (['_metalyser_delivery_id', QTY, FIT, 'Subida al domicilio', 'Acceso comprobado'].some(k => (item.properties?.[k] ?? '') !== expected[k])) {
          if (++repairs > cart.items.length + 5) throw new Error('No se han conservado las opciones de entrega. Actualiza el carrito antes de pagar.');
          cart = await this.request('change', { id: item.key, quantity: item.quantity, properties: expected });
          i = -1; // Property changes can change line keys or merge lines; re-read returned cart.
        }
      }
      const count = totalUnits(cart);
      const config = this.config();
      let fees = cart.items.filter(isService);
      if (count && (!config?.ready || !config.variantId || cart.currency !== 'EUR')) {
        throw new Error('La subida al domicilio no está disponible ahora. Contacta con Metalyser o selecciona entrega a pie de calle.');
      }
      if (!count) {
        if (fees.length) {
          const updates = Object.fromEntries(fees.map(item => [item.key, 0]));
          cart = await this.request('update', { updates });
        }
        const attributes = {};
        for (const key of ATTRS) if (cart.attributes?.[key]) attributes[key] = '';
        if (Object.keys(attributes).length) cart = await this.request('update', { attributes });
        this.drafts = {};
        return cart;
      }
      if (fees.length) {
        const keeper = fees.find(item => item.variant_id === config.variantId);
        const updates = {};
        for (const fee of fees) {
          const qty = fee === keeper ? count : 0;
          if (fee.quantity !== qty) updates[fee.key] = qty;
        }
        if (Object.keys(updates).length) cart = await this.request('update', { updates });
        fees = cart.items.filter(isService);
      }
      if (!fees.length) {
        await this.request('add', { items: [{ id: config.variantId, quantity: count, properties: { '_metalyser_servicio': 'subida-v1' } }] });
        cart = await this.getCart();
      }
      const actual = cart.items.filter(isService);
      if (actual.reduce((sum, item) => sum + item.quantity, 0) !== count ||
          actual.some(item => item.variant_id !== config.variantId) ||
          actual.reduce((sum, item) => sum + item.final_line_price, 0) !== count * PRICE) {
        throw new Error('El cargo de subida no coincide con 40 € por unidad. Revisa los descuentos o contacta con Metalyser antes de pagar.');
      }
      return cart;
    }
    async readSections(ids) {
      // Read the complete current page instead of relying on the partial
      // section response that failed to return the cart markup in this theme.
      // A page GET does not mutate the cart or wake fee recalculation hooks.
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      const response = await fetch(url.href, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('No se ha podido mostrar el carrito actualizado. Comprueba la conexión y vuelve a intentarlo.');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sections = {};
      for (const id of ids) {
        const section = page.getElementById('shopify-section-' + id);
        if (section) sections[id] = section.innerHTML;
      }
      return sections;
    }
    async render(cart) {
      const sections = this.sectionIds();
      if (!sections.length) return cart;
      const fresh = { ...cart, sections: await this.readSections(sections) };
      if (sections.some(id => typeof fresh.sections?.[id] !== 'string')) throw new Error('El carrito se ha actualizado, pero no se ha podido mostrar el nuevo total. Pulsa «Actualizar carrito» o recarga la página.');
      const y = window.scrollY;
      const scrollers = [...document.querySelectorAll('#CartDrawer .drawer__scrollable')].map(el => el.scrollTop);
      const active = document.activeElement;
      const focusedAttr = active?.dataset?.mdAttribute;
      const focusedMode = active?.hasAttribute('data-md-mode') ? active.value : null;
      const focusedChoice = active?.hasAttribute('data-md-delivery-choice') ? active.value : null;
      const focusedKind = active?.hasAttribute('data-md-fit') ? 'fit' : active?.hasAttribute('data-md-count') ? 'count' : active?.hasAttribute('data-md-qty') ? 'qty' : null;
      const rows = [...document.querySelectorAll('[data-md-line]')];
      const focusedRow = rows.indexOf(active?.closest('[data-md-line]'));
      this.rendering = true;
      try { theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'metalyser-delivery', cart: fresh }); }
      finally { this.rendering = false; }
      this.lastCart = fresh;
      this.hydrate();
      window.scrollTo({ top: y, behavior: 'instant' });
      document.querySelectorAll('#CartDrawer .drawer__scrollable').forEach((el, i) => { el.scrollTop = scrollers[i] || 0; });
      let focus;
      if (focusedMode) focus = [...document.querySelectorAll('[data-md-mode]')].find(el => el.value === focusedMode && el.getClientRects().length);
      else if (focusedAttr) focus = [...document.querySelectorAll('[data-md-attribute]')].find(el => el.dataset.mdAttribute === focusedAttr && el.getClientRects().length);
      else if (focusedChoice && focusedRow >= 0) focus = [...(document.querySelectorAll('[data-md-line]')[focusedRow]?.querySelectorAll('[data-md-delivery-choice]') || [])].find(el => el.value === focusedChoice);
      else if (focusedKind && focusedRow >= 0) focus = document.querySelectorAll('[data-md-line]')[focusedRow]?.querySelector('[data-md-' + focusedKind + ']');
      if (focus) focus.focus({ preventScroll: true });
      return fresh;
    }
    run(job, options = {}) {
      const background = options.background === true;
      this.pending++;
      window.MetalyserPriorityTotal?.begin();
      if (!background) { this.setBusy(true); this.status('Actualizando tu entrega…'); }
      const task = this.tail.then(async () => {
        let cart = await this.getCart();
        const originalCart = cart;
        await this.loadEligibility(cart);
        cart = await job(cart);
        cart = await this.persistDrafts(cart);
        cart = await this.reconcile(cart);
        this.lastCart = cart;
        this.hydrate();
        // Reconciliation returns the same cart object unless it actually
        // writes a delivery change. A notification alone must not redraw it.
        const deliveryChanged = cart !== originalCart;
        if (options.render !== false && (options.render !== 'if-changed' || deliveryChanged)) cart = await this.render(cart);
        if (!background || deliveryChanged) this.status('Entrega actualizada.');
        return cart;
      }).catch(async error => {
        // A request might have succeeded before the connection failed. Read, never blindly replay a charge.
        try { this.lastCart = await this.getCart(); if (!background && options.render !== false) await this.render(this.lastCart); } catch (_) { /* Keep the explicit error and retry control. */ }
        this.status(error.message || 'No se ha podido actualizar la entrega. Vuelve a intentarlo.', true);
        throw error;
      }).finally(() => {
        this.pending--;
        window.MetalyserPriorityTotal?.commit(this.lastCart);
        if (!background || (this.pending === 0 && document.documentElement.classList.contains('md-cart-busy'))) {
          this.setBusy(this.pending > 0);
        }
      });
      this.tail = task.catch(() => {});
      return task;
    }
    changeMode(control) {
      const mode = control.value;
      if (!['envio', 'recogida'].includes(mode)) return Promise.reject(new Error('Selecciona una opción de entrega válida.'));
      clearTimeout(this.attrTimer);
      return this.run(cart => this.request('update', { attributes: { [MODE]: mode } }));
    }
    changeDeliveryCard(control) {
      const row = control.closest('[data-md-line]');
      const source = row?.querySelector('[data-md-qty]');
      if (!source) return Promise.reject(new Error('Actualiza el carrito para elegir la entrega.'));
      const quantity = row.querySelector('[data-md-count]');
      const street = control.hasAttribute('data-md-delivery-choice') && control.value === 'street';
      source.value = street ? '0' : (quantity?.value || '1');
      const wrap = row.querySelector('[data-md-count-wrap]');
      if (wrap) wrap.hidden = street;
      return this.changeDelivery(source);
    }
    changeDelivery(control) {
      const row = control.closest('[data-md-line]');
      const key = row.dataset.mdLine;
      const count = Number(row.querySelector('[data-md-qty]').value);
      const fit = row.querySelector('[data-md-fit]').value;
      row.querySelector('[data-md-access-choice]').hidden = count === 0;
      row.querySelector('[data-md-consult]').hidden = fit !== 'consulta';
      return this.run(async cart => {
        if (deliveryMode(cart) !== 'envio') throw new Error('Selecciona «Enviar» antes de elegir la subida a domicilio.');
        const item = cart.items.find(item => item.key === key);
        if (!item) throw new Error('El carrito ha cambiado. Vuelve a seleccionar la entrega de este producto.');
        if (!Number.isInteger(count) || count < 0 || count > item.quantity || !isEligible(item)) throw new Error('La cantidad a subir no es válida.');
        if (!['', 'si', 'escaleras', 'consulta'].includes(fit)) throw new Error('Selecciona una respuesta válida para el acceso.');
        return this.request('change', { id: item.key, quantity: item.quantity, properties: deliveryProperties(item, count, fit) });
      });
    }
    updateQuantity(cartItems, key, quantity, name, target) {
      return this.run(async cart => {
        const item = cart.items.find(item => item.key === key);
        if (!item) {
          // Removing the same line again is harmless if the first request
          // succeeded but the previous screen refresh failed.
          if (Number(quantity) === 0) return cart;
          throw new Error('El carrito ha cambiado. Revisa las cantidades e inténtalo de nuevo.');
        }
        if (isService(item)) throw new Error('Modifica la subida desde las opciones de entrega de cada mueble.');
        const body = { id: item.key, quantity: Number(quantity) };
        if (item.properties?.[QTY] !== undefined && quantity > 0) {
          const next = { ...item, quantity: Number(quantity) };
          body.properties = deliveryProperties(next, isEligible(next) ? units(next) : 0, item.properties[FIT] || '');
        }
        return this.request('change', body);
      }).catch(() => {}).finally(() => { if (cartItems.isConnected) cartItems.disableLoading(key); });
    }
    hydrate() {
      readEligibility(document);
      // Do not trigger DOM observers again for an unchanged delivery state.
      // Also compare the elements so newly rendered sections are hydrated.
      const hydrationNodes = [...document.querySelectorAll('[data-md-mode], [data-md-line], [data-md-panel], [data-md-attribute]')];
      const hydrationKey = JSON.stringify({
        attributes: this.lastCart?.attributes,
        items: this.lastCart?.items.map(item => [item.key, item.quantity, item.product_title, item.properties, isEligible(item)]),
        drafts: this.drafts
      });
      if (this.hydrationKey === hydrationKey && this.hydrationNodes?.length === hydrationNodes.length &&
          hydrationNodes.every((node, index) => node === this.hydrationNodes[index])) return;
      if (this.lastCart) {
        const mode = deliveryMode(this.lastCart);
        document.querySelectorAll('[data-md-mode]').forEach(el => { el.checked = el.value === mode; });
        document.querySelectorAll('[data-md-line]').forEach(el => {
          const item = this.lastCart.items.find(item => item.key === el.dataset.mdLine);
          el.hidden = mode !== 'envio' || !item || !isEligible(item);
        });
        document.querySelectorAll('[data-md-panel]').forEach(el => { el.hidden = mode !== 'envio' || !this.lastCart.items.some(isEligible); });
        document.querySelectorAll('[data-md-mode-hint]').forEach(el => {
          el.textContent = mode === 'recogida' ? 'En el pago, confirma también «Recogida».' : mode === 'envio' ? 'En el pago, selecciona «Envío». Para cambiar a recogida, vuelve a este carrito.' : 'Elige una opción para continuar.';
        });
      }
      for (const el of document.querySelectorAll('[data-md-attribute]')) if (Object.hasOwn(this.drafts, el.dataset.mdAttribute)) el.value = this.drafts[el.dataset.mdAttribute];
      for (const row of document.querySelectorAll('[data-md-line]')) {
        const item = this.lastCart?.items.find(item => item.key === row.dataset.mdLine);
        const consult = row.querySelector('[data-md-consult]');
        if (!item || !consult) continue;
        const count = units(item);
        row.querySelectorAll('[data-md-delivery-choice]').forEach(el => { el.checked = el.value === (count ? 'upstairs' : 'street'); });
        const countWrap = row.querySelector('[data-md-count-wrap]');
        if (countWrap) countWrap.hidden = !count;
        const countControl = row.querySelector('[data-md-count]');
        if (countControl) countControl.value = String(count || 1);
        const props = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_') && !['Subida al domicilio', 'Acceso comprobado'].includes(key));
        const description = props.map(([key, value]) => key + ': ' + value).join('\n');
        const measures = row.querySelector('[data-md-measures]');
        const dimensionProps = props.filter(([key]) => /alto|altura|largo|fondo|di[aá]metro|height|width|depth/i.test(key));
        if (measures && dimensionProps.length) {
          measures.hidden = false;
          measures.textContent = 'Medidas elegidas: ' + dimensionProps.map(([key, value]) => key + ': ' + value).join(' · ');
        }
        const attrs = { ...this.lastCart.attributes, ...this.drafts };
        const access = ATTRS.filter(key => attrs[key]).map(key => key.replace('Entrega - ', '') + ': ' + attrs[key]).join('\n');
        const url = new URL('https://wa.me/34636584391');
        url.searchParams.set('text', ('Hola, quiero comprobar el acceso para subir ' + item.product_title + '.\n' + description + '\n' + access + '\nOs enviaré fotos y medidas del recorrido.').slice(0, 3500));
        consult.href = url.href;
      }
      this.hydrationKey = hydrationKey;
      this.hydrationNodes = hydrationNodes;
    }
    schedule() {
      if (!this.active()) return;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.run(cart => Promise.resolve(cart), { render: 'if-changed', background: true }).catch(() => {});
      }, 350);
    }
    // Validate delivery, then let the theme and fee apps handle checkout.
    // A direct /checkout redirect bypasses apps that add their fee on click.
    async checkout(event, clickedButton = null) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.checkoutRunning) return;
      this.checkoutRunning = true;
      clearTimeout(this.attrTimer);
      clearTimeout(this.refreshTimer);
      const fromClick = event.type === 'click';
      const originalButton = clickedButton || event.submitter || null;
      const originalForm = originalButton?.form || (event.type === 'submit' ? event.target : null);
      try {
        // Keep Chargeup's selected control and its listeners in the DOM.
        const cart = await this.run(cart => Promise.resolve(cart), { render: false });
        if (!cart.items.some(item => !isService(item))) throw new Error('Tu carrito está vacío.');
        const expectedCount = totalUnits(cart);
        const charged = cart.items.filter(isService);
        if (charged.reduce((n, item) => n + item.quantity, 0) !== expectedCount || charged.reduce((n, item) => n + item.final_line_price, 0) !== expectedCount * PRICE) {
          throw new Error('El carrito ha cambiado. Pulsa «Actualizar carrito» y revisa la subida antes de pagar.');
        }
        const issue = validate(cart);
        if (issue) {
          this.status(issue.message, true);
          let focus;
          if (issue.mode) focus = [...document.querySelectorAll('[data-md-mode]')].find(el => el.getClientRects().length);
          if (issue.field) focus = [...document.querySelectorAll('[data-md-attribute]')].find(el => el.dataset.mdAttribute === issue.field && el.getClientRects().length);
          if (issue.key) focus = [...document.querySelectorAll('[data-md-line]')].find(el => el.dataset.mdLine === issue.key)?.querySelector('[data-md-fit]');
          focus?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          focus?.focus({ preventScroll: true });
          return;
        }
        const form = originalForm?.isConnected ? originalForm :
          (originalForm?.id ? document.getElementById(originalForm.id) : null);
        const button = originalButton?.isConnected ? originalButton :
          (form ? Array.from(form.elements).find(el => el.name === 'checkout') : null);
        if (!form || (fromClick && !button) || button?.disabled) {
          throw new Error('El carrito ha cambiado. Revisa la entrega y vuelve a pulsar «Finalizar pedido».');
        }
        // Bypass only our own guard during this synchronous handoff. Other
        // click/submit listeners (including Chargeup) must still run normally.
        this.checkoutHandoff = { form, button };
        try {
          if (fromClick) button.click();
          else if (button) form.requestSubmit(button);
          else form.requestSubmit();
        } finally {
          this.checkoutHandoff = null;
        }
      } catch (error) { this.status(error.message, true); }
      finally { this.checkoutRunning = false; }
    }
    init() {
      document.addEventListener('change', event => {
        if (event.target.matches('[data-md-delivery-choice], [data-md-count]')) { this.changeDeliveryCard(event.target).catch(() => {}); return; }
        if (event.target.matches('[data-md-mode]')) { this.changeMode(event.target).catch(() => {}); return; }
        if (event.target.matches('[data-md-qty], [data-md-fit]')) this.changeDelivery(event.target).catch(() => {});
      });
      const draft = event => {
        const el = event.target;
        if (!el.matches('[data-md-attribute]')) return;
        this.drafts[el.dataset.mdAttribute] = el.value.slice(0, 500);
        this.hydrate(); clearTimeout(this.attrTimer);
        this.attrTimer = setTimeout(() => this.run(cart => Promise.resolve(cart), { render: false }).catch(() => {}), 800);
      };
      document.addEventListener('input', draft); document.addEventListener('change', draft);
      document.addEventListener('click', event => {
        if (!this.active()) return;
        if (event.target.closest('[data-md-retry]')) {
          event.preventDefault();
          clearTimeout(this.refreshTimer);
          this.run(cart => Promise.resolve(cart), { render: true }).catch(() => {});
          return;
        }
        const checkout = event.target.closest('button[name="checkout"],input[name="checkout"]');
        if (checkout && checkout === this.checkoutHandoff?.button) return;
        if (checkout && checkout.closest('#CartDrawer, .cart')) this.checkout(event, checkout);
      }, true);
      document.addEventListener('submit', event => {
        if (!this.active()) return;
        const form = event.target;
        if (form === this.checkoutHandoff?.form) return;
        if ((form.id === 'cart' && event.submitter?.name !== 'update') || form.matches('.drawer__footer-buttons')) this.checkout(event);
      }, true);
      document.addEventListener('cart:updated', () => { if (!this.rendering) this.schedule(); });
      document.addEventListener('shopify:section:load', () => this.schedule());
      window.addEventListener('pageshow', event => { if (event.persisted) this.schedule(); });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.schedule(); });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.schedule(), { once: true });
      else this.schedule();
    }
  }
  window.MetalyserDelivery = new Delivery();
})();

/* Metalyser: one committed cart for priority and upstairs delivery totals.
   Shopify owns the service lines; ChargeUp owns its fee and checkout. */
(() => {
  if (window.MetalyserPriorityTotal) return;
  let scheduled = false, latestCart = null, held = null;
  const labels = new WeakMap();
  const normal = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const valid = cart => Array.isArray(cart?.items) && Number.isSafeInteger(cart.total_price);
  const delivery = () => window.MetalyserDelivery;
  const busy = () => (delivery()?.pending || 0) > 0;
  const managed = () => delivery()?.active?.() === true;
  // Compact presentation only in the side cart. ChargeUp keeps its own
  // widget, selector, fee calculation and checkout behaviour.
  function installCompactPriorityStyle() {
    if (document.getElementById('metalyser-priority-drawer-style')) return;
    const style = document.createElement('style');
    style.id = 'metalyser-priority-drawer-style';
    style.textContent = `
      #CartDrawer [data-metalyser-priority-compact] {
        box-sizing: border-box !important;
        width: 100% !important;
        min-width: 0 !important;
        height: auto !important;
        min-height: 0 !important;
        margin: 6px 0 !important;
        padding: 6px 10px !important;
        border: 1px solid #e4dada;
        border-radius: 8px !important;
        font-family: inherit !important;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__wrapper {
        display: flex !important;
        align-items: center !important;
        gap: 6px !important;
        width: 100% !important;
        max-width: none !important;
        height: auto !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__button {
        position: relative !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        flex: 0 0 44px !important;
        width: 44px !important;
        height: 44px !important;
        min-height: 44px !important;
        padding: 0 !important;
        margin: 0 !important;
        cursor: pointer;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__button:focus-visible {
        outline: 2px solid #7B5454;
        outline-offset: 1px;
        border-radius: 5px;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__content {
        flex: 1 1 0 !important;
        min-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__content__title {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        flex-wrap: wrap !important;
        gap: 4px 10px !important;
        padding: 0 !important;
        margin: 0 !important;
        font-size: 14px !important;
        line-height: 1.3 !important;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__content__title__text {
        display: block !important;
        flex: 1 1 130px !important;
        min-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        font-size: 14px !important;
        line-height: 1.3 !important;
        font-weight: 600 !important;
        white-space: normal !important;
        overflow-wrap: anywhere;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__content__title__icon {
        display: block !important;
        position: static !important;
        padding: 0 !important;
        margin: 2px 0 0 !important;
        cursor: default;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__content__title__icon > svg {
        display: none !important;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__content__info {
        display: block !important;
        position: static !important;
        visibility: visible !important;
        opacity: 1 !important;
        transform: none !important;
        width: auto !important;
        height: auto !important;
        padding: 0 !important;
        margin: 0 !important;
        background: transparent !important;
        color: #655b5b !important;
        font-size: 13px !important;
        font-weight: 400 !important;
        line-height: 1.3 !important;
        text-align: left !important;
        white-space: normal !important;
        overflow: visible !important;
      }
      #CartDrawer [data-metalyser-priority-compact] .charge-up-widget__price {
        flex: 0 0 auto !important;
        padding: 0 !important;
        margin: 0 !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 1.3 !important;
        white-space: nowrap !important;
      }
    `;
    document.head.appendChild(style);
  }
  function api() {
    try {
      if (typeof CUV1_getChargeOption !== 'function' || typeof CUV1_getFees !== 'function' || typeof CUV1_getFeeStatus !== 'function') return null;
      const state = CUV1_getChargeOption();
      if (!state.extensionDataLoaded || state.settings?.extension_status !== 'enabled') return null;
      return { state, fees: CUV1_getFees().filter(fee => /entrega prioritaria/.test(normal(fee.title))) };
    } catch { return null; }
  }
  function widgets(fee) { return [...document.querySelectorAll('.chargeUp-charge')].filter(el => el.id.endsWith('-' + fee.id)); }
  function containsFee(item, id) {
    return String(item.variant_id || item.id) === String(id) ||
      [...item.item_components || [], ...item.components || []].some(part => containsFee(part, id));
  }
  function amountFor(fee, cart) {
    // These are ChargeUp's own calculation helpers, using the SAME cart as the
    // total. Never combine a fresh Shopify total with the app's previous base.
    if (typeof CUV1_makeDivisible !== 'function') return null;
    const type = fee.fee_type || 'fixed';
    let raw;
    if (type === 'percentage' && typeof CUV1_getPercentageBase === 'function') raw = CUV1_getPercentageBase(fee, cart) * (parseFloat(fee.fee_amount) || 0) / 100;
    else if (type === 'fixed') raw = parseFloat(fee.fee_amount) || 0;
    else if (type === 'per_product' && typeof CUV1_getPerProductAmount === 'function') raw = CUV1_getPerProductAmount(fee, cart);
    else if (type === 'delivery' && typeof CUV1_getDeliveryAmount === 'function') raw = CUV1_getDeliveryAmount(fee, cart);
    else return null;
    const amount = CUV1_makeDivisible(raw, parseFloat(fee.product_price) || 0);
    return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : null;
  }
  function format(cents, currency) { return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(cents / 100); }
  function put(node, value) { if (node && node.textContent !== value) node.textContent = value; }
  function show(snapshot, pending) {
    if (!snapshot) return;
    for (const element of document.querySelectorAll('.totals__subtotal-value')) {
      put(element, snapshot.text);
      if (element.getAttribute('aria-live') !== 'polite') element.setAttribute('aria-live', 'polite');
      if (element.getAttribute('aria-atomic') !== 'true') element.setAttribute('aria-atomic', 'true');
      if (element.getAttribute('aria-busy') !== String(pending)) element.setAttribute('aria-busy', String(pending));
      const label = element.closest('.totals')?.querySelector('.totals__subtotal');
      if (label) {
        if (!labels.has(label)) labels.set(label, label.textContent);
        put(label, snapshot.selected ? 'Total con entrega prioritaria' : labels.get(label));
      }
    }
    for (const [id, text] of snapshot.prices) {
      for (const widget of widgets({ id })) put(widget.querySelector('.charge-up-widget__price'), text);
    }
  }
  function render() {
    scheduled = false;
    if (busy()) { show(held, true); return; }
    const current = api(); if (!current) return;
    const cart = latestCart || (managed() && delivery().lastCart) || current.state.cartData;
    if (!valid(cart)) return;
    const currency = cart.currency || 'EUR';
    let extra = 0, selected = false;
    const prices = [];
    for (const fee of current.fees) {
      const enabled = CUV1_getFeeStatus(fee.id) || fee.enable_checkbox === 'disabled';
      for (const widget of widgets(fee)) {
        if (widget.closest('#CartDrawer') && !widget.hasAttribute('data-metalyser-priority-compact')) {
          widget.setAttribute('data-metalyser-priority-compact', '');
        }
        const button = widget.querySelector('.charge-up-widget__button');
        if (button && fee.enable_checkbox !== 'disabled') {
          if (button.getAttribute('role') !== 'switch') button.setAttribute('role', 'switch');
          if (button.tabIndex !== 0) button.tabIndex = 0;
          if (button.getAttribute('aria-label') !== fee.title) button.setAttribute('aria-label', fee.title);
          if (button.getAttribute('aria-checked') !== String(enabled)) button.setAttribute('aria-checked', String(enabled));
        }
      }
      if (typeof CUV1_evaluateConditionalRules === 'function' && !CUV1_evaluateConditionalRules(cart, fee)) continue;
      if (typeof CUV1_feeApplies === 'function' && !CUV1_feeApplies(fee, cart)) continue;
      const amount = amountFor(fee, cart); if (amount === null) return;
      prices.push([fee.id, format(amount, currency)]);
      if (!enabled || !cart.items.length) continue;
      selected = true;
      if (!cart.items.some(item => containsFee(item, fee.variant_id))) extra += amount;
    }
    held = { text: format(cart.total_price + extra, currency), selected, prices };
    show(held, false);
  }
  function schedule() { if (!scheduled) { scheduled = true; requestAnimationFrame(render); } }
  function receive(event) {
    // While the delivery controller is active, only its completed transaction
    // can commit a cart. ChargeUp sees every intermediate change/add/GET too.
    if (!managed() && !busy()) {
      const cart = event.detail?.data || event.detail?.cart;
      if (valid(cart)) latestCart = cart;
    }
    // Section replacement emits cart:updated synchronously. Restore the held
    // value in that same turn, before the browser can paint the raw subtotal.
    if (managed()) show(held, busy());
    schedule();
  }
  function begin() {
    if (!held) {
      const node = document.querySelector('.totals__subtotal-value');
      if (node) held = { text: node.textContent, selected: false, prices: [] };
    }
    show(held, true);
  }
  function commit(cart) {
    if (valid(cart)) latestCart = cart;
    if (busy()) return;
    // Synchronize the native checkout calculation as well as the visible sum.
    if (valid(latestCart) && api() && typeof CUV1_setChargeUpOption === 'function') {
      CUV1_setChargeUpOption({ cartData: latestCart });
      if (!document.querySelector('.chargeUp-charge') && typeof CUV1_rp === 'function') CUV1_rp();
    }
    render();
  }
  function toggle(event) {
    if (event.type === 'keydown' && (![' ', 'Enter'].includes(event.key) || event.repeat)) return;
    const button = event.target.closest?.('.charge-up-widget__button'); if (!button) return;
    const widget = button.closest('.chargeUp-charge'), current = api();
    const fee = current?.fees.find(fee => widget?.id.endsWith('-' + fee.id));
    if (!fee || fee.enable_checkbox === 'disabled' || typeof CUV1_setFeeStatus !== 'function') return;
    event.preventDefault(); event.stopImmediatePropagation();
    CUV1_setFeeStatus(fee.id, !CUV1_getFeeStatus(fee.id));
    schedule();
    if (event.type === 'keydown') requestAnimationFrame(() => requestAnimationFrame(() => widgets(fee)[0]?.querySelector('.charge-up-widget__button')?.focus({ preventScroll: true })));
  }
  function start() {
    installCompactPriorityStyle();
    const observer = new MutationObserver(records => {
      // Watch widget state and replaced sections, not our own total text.
      if (records.some(record => record.type === 'attributes' ? record.target.matches?.('.charge-up-switch') :
        [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
          (node.matches('.chargeUp-charge,.totals,.totals__subtotal-value') || node.querySelector('.chargeUp-charge,.totals,.totals__subtotal-value'))))) schedule();
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    schedule();
  }
  document.addEventListener('click', toggle, true);
  document.addEventListener('keydown', toggle, true);
  window.addEventListener('charge-up-cart-update', receive);
  document.addEventListener('cart:updated', receive);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', () => { if (!managed()) latestCart = null; schedule(); });
  window.MetalyserPriorityTotal = { refresh: schedule, begin, commit };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
